import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Container, Grid, Card, CardContent,
  Stack, Divider, useTheme, useMediaQuery,
} from '@mui/material';
import {
  Agriculture, Payment, Assessment, People,
  CheckCircle, ArrowForward,
} from '@mui/icons-material';

const features = [
  {
    icon: <Agriculture sx={{ fontSize: 36 }} />,
    title: 'Farm Operations',
    desc: 'Track crops, livestock, and seasonal operations from planning to harvest.',
  },
  {
    icon: <Payment sx={{ fontSize: 36 }} />,
    title: 'Payment Management',
    desc: 'Process salaries, contractor fees, and expense requests in one place.',
  },
  {
    icon: <Assessment sx={{ fontSize: 36 }} />,
    title: 'Financial Reports',
    desc: 'Monthly summaries, budget tracking, and expense breakdowns at a glance.',
  },
  {
    icon: <People sx={{ fontSize: 36 }} />,
    title: 'Staff & Contractors',
    desc: 'Manage employees, payroll schedules, and contractor relationships.',
  },
];

const benefits = [
  'Multi-project budget tracking with real-time spend alerts',
  'Automated payroll reminders and salary payment processing',
  'Expense approval workflows with receipt tracking',
  'Role-based access for owners, admins, and supervisors',
  'Mobile-friendly — manage your farm from anywhere',
];

export const LandingPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const token = localStorage.getItem('jwt_token');

  const handleCTA = () => {
    navigate(token ? '/dashboard' : '/register');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Nav */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          px: { xs: 2, sm: 4 },
          py: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Agriculture sx={{ fontSize: 28 }} />
          <Typography variant="h6" fontWeight="bold">SmartGrow</Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          {!token && (
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              onClick={() => navigate('/register')}
            >
              Register
            </Button>
          )}
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            onClick={() => navigate(token ? '/dashboard' : '/login')}
            endIcon={<ArrowForward />}
          >
            {token ? 'Go to Dashboard' : 'Login'}
          </Button>
        </Stack>
      </Box>

      {/* Hero */}
      <Box
        sx={{
          bgcolor: 'primary.dark',
          color: 'white',
          py: { xs: 6, md: 10 },
          px: { xs: 2, sm: 4 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant={isMobile ? 'h4' : 'h2'}
            fontWeight="bold"
            gutterBottom
            sx={{ lineHeight: 1.2 }}
          >
            Farm Management,{' '}
            <Box component="span" sx={{ color: 'secondary.light' }}>
              Simplified
            </Box>
          </Typography>
          <Typography
            variant={isMobile ? 'body1' : 'h6'}
            sx={{ mb: 4, opacity: 0.9, maxWidth: 600, mx: 'auto' }}
          >
            SmartGrow helps farm owners and managers track operations, control budgets,
            and pay workers — all from one platform.
          </Typography>
          <Stack direction={isMobile ? 'column' : 'row'} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              color="secondary"
              size="large"
              onClick={handleCTA}
              endIcon={<ArrowForward />}
              sx={{ px: 4, py: 1.5, fontSize: '1rem' }}
            >
              {token ? 'Go to Dashboard' : 'Get Started'}
            </Button>
            {!token && (
              <Button
                variant="outlined"
                color="inherit"
                size="large"
                onClick={() => navigate('/login')}
                sx={{ px: 4, py: 1.5, fontSize: '1rem' }}
              >
                Login
              </Button>
            )}
          </Stack>
        </Container>
      </Box>

      {/* Features */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Typography
          variant={isMobile ? 'h5' : 'h4'}
          fontWeight="bold"
          textAlign="center"
          gutterBottom
        >
          Everything you need to run your farm
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          textAlign="center"
          sx={{ mb: 5 }}
        >
          Built for Nigerian farm operators managing multiple projects and teams.
        </Typography>

        <Grid container spacing={3}>
          {features.map((f) => (
            <Grid key={f.title} item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      color: 'primary.main',
                      mb: 2,
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    {f.icon}
                  </Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {f.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {f.desc}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Divider />

      {/* Benefits */}
      <Box sx={{ bgcolor: 'grey.50', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant={isMobile ? 'h5' : 'h4'}
                fontWeight="bold"
                gutterBottom
              >
                Why SmartGrow?
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Designed from the ground up for agricultural businesses in Nigeria.
              </Typography>
              <Stack spacing={1.5}>
                {benefits.map((b) => (
                  <Stack key={b} direction="row" spacing={1.5} alignItems="flex-start">
                    <CheckCircle color="success" sx={{ mt: 0.2, flexShrink: 0 }} />
                    <Typography variant="body2">{b}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Ready to take control?
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 3 }}>
                    Create a free account or log in to start managing your farm operations today.
                  </Typography>
                  <Stack spacing={1.5}>
                    <Button
                      variant="contained"
                      color="secondary"
                      fullWidth
                      size="large"
                      onClick={() => navigate('/register')}
                      endIcon={<ArrowForward />}
                      sx={{ py: 1.5 }}
                    >
                      Get Started — It's Free
                    </Button>
                    <Button
                      variant="outlined"
                      color="inherit"
                      fullWidth
                      size="large"
                      onClick={() => navigate('/login')}
                      sx={{ py: 1.5, borderColor: 'rgba(255,255,255,0.5)' }}
                    >
                      Login
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          bgcolor: 'primary.dark',
          color: 'white',
          py: 3,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          © {new Date().getFullYear()} SmartGrow · Farm Management Platform
        </Typography>
      </Box>
    </Box>
  );
};
